import { AlertTriangle, ArrowRight, Clock, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const TONE = { danger: "text-rose-700 dark:text-rose-300", warning: "text-amber-700 dark:text-amber-300" };

const Row = ({ item, onGo, testId }) => {
  const clickable = Boolean(item.tab || item.action || item.link);
  const Comp = clickable ? "button" : "div";
  return (
    <Comp type={clickable ? "button" : undefined} onClick={clickable ? () => onGo(item) : undefined} data-testid={testId}
      className={cn("group flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left -mx-2", clickable && "transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none")}>
      <div className="min-w-0 flex-1">
        <div className={cn("text-[13px] font-medium leading-5", TONE[item.tone])}>{item.text}</div>
        {item.sub && <div className="line-clamp-2 text-[12px] leading-4 text-muted-foreground">{item.sub}</div>}
      </div>
      {clickable && <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />}
    </Comp>
  );
};

const Column = ({ icon: Icon, title, items, empty, tone, onGo, testId }) => (
  <section className="min-w-0" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11.5px] font-medium text-muted-foreground">
      <Icon className={cn("h-3.5 w-3.5", tone)} />
      <span>{title}</span>
      {items.length > 0 && <span className="ml-1 rounded bg-muted px-1.5 font-mono text-[10.5px] tabular text-foreground/70">{items.length}</span>}
    </div>
    <div className="mt-1.5 space-y-0.5">
      {items.length ? items.map((it) => <Row key={it.key} item={it} onGo={onGo} testId={`${testId}-${it.key}`} />) : <div className="px-0 py-1.5 text-[13px] text-muted-foreground/80">{empty}</div>}
    </div>
  </section>
);

/**
 * «Nå» – the phase-aware operational state that replaces the zero-KPI strip:
 * what blocks the project, what happens next, and what we are waiting for. Items are deterministic (projectLens).
 */
export const NowPanel = ({ now, onGo, className, testId = "project-now-panel", compact = false }) => {
  const cols = [
    { key: "blockers", icon: AlertTriangle, title: "Blokkerer", items: now.blockers, empty: "Ingen blokkeringer", tone: now.blockers.length ? "text-rose-600" : "" },
    { key: "next", icon: ArrowRight, title: "Neste", items: now.next, empty: "Velg neste steg", tone: "text-primary" },
    { key: "waiting", icon: Clock, title: "Venter på", items: now.waiting, empty: "Ingenting å vente på", tone: "" },
  ].filter((c) => !compact || c.items.length);
  if (!cols.length) return null;
  return (
    <div className={cn("grid gap-5 rounded-[var(--radius)] border border-border/60 bg-card p-4 sm:p-5 md:gap-6 md:divide-x md:divide-border/60 [&>section+section]:md:pl-6", cols.length === 3 ? "md:grid-cols-3" : cols.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1", className)} data-testid={testId} data-columns={cols.length}>
      {cols.map((c) => <Column key={c.key} icon={c.icon} title={c.title} items={c.items} empty={c.empty} tone={c.tone} onGo={onGo} testId={`${testId}-${c.key}`} />)}
    </div>
  );
};
